import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { authAPI } from '../services/api';

const ConfirmPasswordReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!token) {
      setError('Token non valido o mancante');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (password.length < 6) {
      setError('La password deve contenere almeno 6 caratteri');
      return;
    }

    if (!token) {
      setError('Token non valido o mancante');
      return;
    }

    setLoading(true);

    try {
      await authAPI.confirmPasswordReset(token, password);
      setSuccess(true);
      setError('');
      
      // Aspetta 2 secondi per mostrare il messaggio di successo, poi naviga
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Password reimpostata con successo! Effettua il login.' } 
        });
      }, 2000);
    } catch (err: any) {
      // Errore('Errore reset password:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Errore durante il reset della password';
      setError(errorMessage);
      setSuccess(false);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/calendar-5983133_1920.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(13, 71, 161, 0.7)',
        }
      }}
    >
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              padding: 4, 
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography component="h1" variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Nuova Password
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
                Inserisci la tua nuova password.
              </Typography>

              {success && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  Password reimpostata con successo! Verrai reindirizzato al login...
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Nuova Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || !token || success}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Conferma Password"
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading || !token || success}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    mt: 3, 
                    mb: 2,
                    backgroundColor: '#0d47a1',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                  disabled={loading || !token || success}
                >
                  {loading ? <CircularProgress size={24} /> : 'Reimposta Password'}
                </Button>
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  Torna al Login
                </Link>
              </Box>
          </Box>
        </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ConfirmPasswordReset;

